/* eslint-disable camelcase */
import { useMutation } from '@tanstack/react-query'
import { ConnectionModel, ReactQueryKeys, SearchConnection } from '@/types'
import { useContext, useMemo } from 'react'
import { AxiosContext } from '@/context/Axios'
import { CircleType, RequestActionType } from '@/utils/constants'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { useAuth } from '@/hooks/common'
import { useGetMyConnections } from './useGetMyConnections'

interface InviteActionParam {
  connID: string
  connReqID: string
  connection?: ConnectionModel | null
}

export const useManageConnections = () => {
  const axios = useContext(AxiosContext)
  const { getMyConnections } = useGetMyConnections()

  const { user: currentUser, basicInfo, setUserBasicInfo } = useAuth()

  const [myGroups] = useAppStore((state) => [state.user.myGroups], shallow)
  const { setSelectedUserProfile } = useAppStore()

  const [selectedUserProfile] = useAppStore((state) => [state.user.selectedUserProfile], shallow)
  const [searchConnections] = useAppStore((state) => [state.user.searchConnections], shallow)
  const { setSearchConnections } = useAppStore()
  const [connRequests] = useAppStore((state) => [state.user.connRequests], shallow)
  const { setConnRequests } = useAppStore()

  const { setMyConnections } = useAppStore()
  const [myConnections] = useAppStore((state) => [state.user.myConnections], shallow)

  const [userConnections] = useAppStore((state) => [state.user.userConnections], shallow)
  const { setUserConnections } = useAppStore()

  const friendsGroupID = useMemo(() => {
    const friendCircle = myGroups.find(
      (e) => e.isSystemProvided === '1' && e.circleTypeID == `${CircleType.friend.id}`,
    )
    return friendCircle?.circleID
  }, [myGroups])

  const acceptConnectionInvite = useMutation({
    mutationKey: [ReactQueryKeys.ACCEPT_CONNECTION],
    mutationFn: async (param: InviteActionParam) => {
      if (friendsGroupID) {
        const { connID, connReqID } = param

        const response = await axios.api.post('ajax/connections', {
          requestType: 'connReqUpdate',
          connReqID,
          cliqueIDs: [friendsGroupID],
          connReqStatus: RequestActionType.ACCEPT,
          format: 'json',
        })

        if (!response.data?.isError) {
          // Update search results

          if (searchConnections[connID]) {
            setSearchConnections({
              ...searchConnections,
              [connID]: {
                ...searchConnections[connID],
                reqSent: false,
                reqReceived: false,
                isConnection: true,
              },
            })
          }

          // Update Other User Profile
          if (selectedUserProfile?.userID == connID) {
            setSelectedUserProfile({
              ...selectedUserProfile,
              reqSent: false,
              reqReceived: false,
              isConnection: true,
              reqSentConnReqID: 0,
              reqReceivedConnReqID: 0,
            })
          }

          // update dataset in connection list of other user
          const connIndex = (userConnections || []).findIndex((e) => e.userID == connID)
          if (connIndex >= 0) {
            let mapped = userConnections.map((e) => {
              return e.userID != connID
                ? e
                : {
                    ...e,
                    reqSent: false,
                    reqReceived: false,
                    isConnection: true,
                  }
            })
            setUserConnections(mapped)
          }

          // Remove from connection Requests
          let filtered = connRequests.filter((e) => e.requestorID != connID)
          setConnRequests(filtered)
        }

        // Update my connections
        // Updating connCount in basic info and refresing the list

        setUserBasicInfo({
          ...basicInfo,
          connCount: (Number(basicInfo.connCount) || 0) + 1,
        })
        getMyConnections.mutateAsync(1)

        return response.data
      }
    },
  })

  const rejectConnectionInvite = useMutation({
    mutationKey: [ReactQueryKeys.DECLINE_CONNECTION],
    mutationFn: async (param: InviteActionParam) => {
      const { connID, connReqID } = param
      const response = await axios.api.post('ajax/connections', {
        requestType: 'connReqUpdate',
        connReqID: connReqID,
        cliqueIDs: [],
        connReqStatus: RequestActionType.REJECT,
        format: 'json',
      })

      if (!response.data?.isError) {
        // Update search results
        if (searchConnections[connID]) {
          setSearchConnections({
            ...searchConnections,
            [connID]: {
              ...searchConnections[connID],
              reqSent: false,
              reqReceived: false,
              isConnection: false,
            },
          })
        }

        // Update Other User Profile
        if (selectedUserProfile?.userID == connID) {
          setSelectedUserProfile({
            ...selectedUserProfile,
            reqSent: false,
            reqReceived: false,
            isConnection: false,
            reqSentConnReqID: 0,
            reqReceivedConnReqID: 0,
          })
        }

        // Remove from connection Requests
        let filtered = connRequests.filter((e) => e.requestorID != connID)
        setConnRequests(filtered)
      }

      // update dataset in connection list of other user
      const connIndex = (userConnections || []).findIndex((e) => e.userID == connID)
      if (connIndex >= 0) {
        let mapped = userConnections.map((e) => {
          return e.userID != connID
            ? e
            : {
                ...e,
                reqSent: false,
                reqReceived: false,
                isConnection: false,
              }
        })
        setUserConnections(mapped)
      }

      return response.data
    },
  })

  const sendFollowRequest = useMutation({
    mutationKey: [ReactQueryKeys.ADD_CONNECTION],
    mutationFn: async (connID: string) => {
      if (friendsGroupID) {
        const response = await axios.api.post('ajax/connections', {
          requestType: 'connReqAdd',
          connID: connID,
          cliqueIDs: [friendsGroupID],
          format: 'json',
        })
        if (!response.data?.isError) {
          if (searchConnections[connID]) {
            setSearchConnections({
              ...searchConnections,
              [connID]: {
                ...searchConnections[connID],
                reqSent: true,
                reqReceived: false,
              },
            })
          }

          // Update Other User Profile
          if (selectedUserProfile?.userID == connID) {
            setSelectedUserProfile({
              ...selectedUserProfile,
              reqReceived: true,
              reqSent: false,
              reqSentConnReqID: 0,
              reqReceivedConnReqID: 0,
            })
          }

          // update dataset in connection list of other user
          const connIndex = (userConnections || []).findIndex((e) => e.userID == connID)
          if (connIndex >= 0) {
            let mapped = userConnections.map((e) => {
              return e.userID != connID
                ? e
                : {
                    ...e,
                    reqSent: true,
                    reqReceived: false,
                  }
            })
            setUserConnections(mapped)
          }
        }
        return response.data
      }
    },
  })

  const removeConnection = useMutation({
    mutationKey: [ReactQueryKeys.DELETE_CONNECTION],
    mutationFn: async (connID: string) => {
      const response = await axios.api.post('ajax/circles', {
        requestType: 'delConn',
        connID,
        format: 'json',
      })
      if (!response.data?.isError) {
        if (searchConnections[connID]) {
          setSearchConnections({
            ...searchConnections,
            [connID]: {
              ...searchConnections[connID],
              reqSent: false,
              reqReceived: false,
              isConnection: false,
            },
          })
        }

        // Update Other User Profile
        if (selectedUserProfile?.userID == connID) {
          setSelectedUserProfile({
            ...selectedUserProfile,
            reqSent: false,
            reqReceived: false,
            isConnection: false,
            reqSentConnReqID: 0,
            reqReceivedConnReqID: 0,
          })
        }

        // Remove from my connections list
        const index = myConnections.findIndex((i) => i.userID === connID)
        if (index >= 0) {
          setMyConnections(myConnections.filter((e) => e.userID !== connID))
        }
        setUserBasicInfo({
          ...basicInfo,
          connCount: (Number(basicInfo.connCount) || 0) - 1,
        })

        // update dataset in connection list of other user
        const connIndex = (userConnections || []).findIndex((e) => e.userID == connID)
        if (connIndex >= 0) {
          let mapped = userConnections.map((e) => {
            return e.userID != connID
              ? e
              : {
                  ...e,
                  reqSent: false,
                  reqReceived: false,
                  isConnection: false,
                }
          })
          setUserConnections(mapped)
        }
      }
      return response.data
    },
  })

  const blockConnection = useMutation({
    mutationKey: [ReactQueryKeys.BLOCK_CONNECTION],
    mutationFn: async (connID: string) => {
      const response = await axios.api.post('ajax/circles', {
        requestType: 'addUserBlock',
        onUserID: connID,
        fromUserID: currentUser.userID,
        reason: 'I want to report this user',
        reportUserID: currentUser.userID,
        format: 'json',
      })
      if (!response.data?.isError) {
        if (searchConnections[connID]) {
          setSearchConnections({
            ...searchConnections,
            [connID]: {
              ...searchConnections[connID],
              reqSent: false,
              reqReceived: false,
              isConnection: false,
            },
          })
        }

        // Update Other User Profile
        if (selectedUserProfile?.userID == connID) {
          setSelectedUserProfile({
            ...selectedUserProfile,
            reqSent: false,
            reqReceived: false,
            isConnection: false,
            reqSentConnReqID: 0,
            reqReceivedConnReqID: 0,
          })
        }

        const index = myConnections.findIndex((i) => i.userID === connID)
        if (index >= 0) {
          setMyConnections(myConnections.filter((e) => e.userID !== connID))
        }
        setUserBasicInfo({
          ...basicInfo,
          connCount: (Number(basicInfo.connCount) || 0) - 1,
        })

        // update dataset in connection list of other user
        const connIndex = (userConnections || []).findIndex((e) => e.userID == connID)
        if (connIndex >= 0) {
          let mapped = userConnections.map((e) => {
            return e.userID != connID
              ? e
              : {
                  ...e,
                  reqSent: false,
                  reqReceived: false,
                  isConnection: false,
                }
          })
          setUserConnections(mapped)
        }
      }
      return response.data
    },
  })

  return {
    acceptConnectionInvite,
    rejectConnectionInvite,
    sendFollowRequest,
    removeConnection,
    blockConnection,
  }
}
